import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Activity,
  CircleDot,
  Command,
  MessageCircle,
  Slash,
} from "lucide-react";
import { selectAppIsAuthenticated } from "../redux/appAuthSlice";
import { formatBadgeCount } from "../utils/formatBadgeCount";
import AppLiveClock from "./ui/AppLiveClock";

const SEGMENT_TITLES = {
  bethouse: "House",
  p2p: "P2P",
  "bond-predict": "BondPredict",
  "bond-play": "Play",
  "bond-coin": "Bond Coin",
  "bond-dice": "Bond Dice",
  "bond-numbers": "Bond Numbers",
  predictions: "Tips",
  leaderboard: "Leaderboard",
  referrals: "Referrals",
  "general-chat": "BondChat",
  activity: "Bond Pulse",
  dashboard: "Dashboard",
  "joined-bets": "Joined bets",
  requests: "Requests",
  profile: "Profile",
  wallet: "Wallet",
  settings: "Settings",
  privacy: "Privacy",
  terms: "Terms",
  community: "Community",
  manage: "Manage",
  create: "Create",
  place: "Place bet",
  portfolio: "Portfolio",
  "bond-wheel": "Bond wheel",
  connections: "Connections",
  followers: "Followers",
  following: "Following",
};

const isMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);

const titleFor = (segment) => {
  if (!segment) return "Workspace";
  if (SEGMENT_TITLES[segment]) return SEGMENT_TITLES[segment];
  if (/^[a-f0-9]{18,}$/i.test(segment)) return null;
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const buildCrumbs = (pathname) => {
  const segs = pathname.replace(/^\/app\/?/, "").split("/").filter(Boolean);

  if (
    segs[0] === "profile" &&
    segs[1] === "connections" &&
    (segs[2] === "followers" || segs[2] === "following")
  ) {
    return [
      { label: "Profile", to: "/app/profile" },
      {
        label: segs[2] === "following" ? "Following" : "Followers",
        to: `/app/profile/connections/${segs[2]}`,
      },
    ];
  }

  const crumbs = [];
  segs.forEach((seg, idx) => {
    const label = titleFor(seg);
    if (!label) return;
    const path = "/app/" + segs.slice(0, idx + 1).join("/");
    crumbs.push({ label, to: path });
  });
  if (crumbs.length === 0) crumbs.push({ label: "Floor", to: "/app" });
  return crumbs;
};

const AppWorkspaceTopStrip = ({ onOpenPalette }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const chatUnread = useSelector((s) => s.generalChat?.unreadCount || 0);
  const activityUnread = useSelector((s) => s.activities?.unreadCount || 0);
  const socketConnected = useSelector(
    (s) => s.generalChat?.connected || false
  );

  const crumbs = useMemo(() => buildCrumbs(pathname), [pathname]);
  const isPolicyRoute =
    pathname === "/app/privacy" ||
    pathname === "/app/terms" ||
    pathname === "/app/community";
  const isProfileRoute = pathname.startsWith("/app/profile");
  const isBondPredictSubRoute = pathname.startsWith("/app/bond-predict/");
  const isPredCreateRoute = pathname.startsWith("/app/predictions/create");
  const showBack =
    !isPolicyRoute &&
    !isProfileRoute &&
    !isBondPredictSubRoute &&
    !isPredCreateRoute &&
    pathname !== "/app" &&
    pathname !== "/app/bethouse" &&
    pathname !== "/app/p2p" &&
    pathname !== "/app/bond-predict" &&
    pathname !== "/app/bond-play" &&
    pathname !== "/app/bond-coin" &&
    pathname !== "/app/bond-dice" &&
    pathname !== "/app/bond-numbers" &&
    pathname !== "/app/predictions" &&
    pathname !== "/app/leaderboard" &&
    pathname !== "/app/referrals";

  const kbdHint = isMac() ? "⌘K" : "Ctrl K";

  return (
    <div className="wsstrip" role="region" aria-label="Workspace context">
      <div className="wsstrip-left">
        {showBack && (
          <button
            type="button"
            className="wsstrip-back"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            title="Back"
          >
            <ArrowLeft size={13} />
          </button>
        )}
        <nav className="wsstrip-crumbs" aria-label="Breadcrumb">
          <span className="wsstrip-crumbs-root">Desk</span>
          {crumbs.map((crumb, idx) => (
            <React.Fragment key={crumb.to}>
              <Slash size={11} className="wsstrip-crumbs-sep" aria-hidden />
              <button
                type="button"
                className={`wsstrip-crumb ${
                  idx === crumbs.length - 1 ? "wsstrip-crumb-current" : ""
                }`}
                onClick={() => navigate(crumb.to)}
              >
                {crumb.label}
              </button>
            </React.Fragment>
          ))}
        </nav>
      </div>

      <div className="wsstrip-center">
        <AppLiveClock className="app-chrome-clock" />
      </div>

      <div className="wsstrip-right">
        <span
          className={`wsstrip-live ${
            socketConnected ? "wsstrip-live-on" : "wsstrip-live-off"
          }`}
          title={socketConnected ? "Live · streaming updates" : "Reconnecting"}
          aria-label={socketConnected ? "Live" : "Reconnecting"}
        >
          <CircleDot size={10} aria-hidden />
        </span>

        <button
          type="button"
          onClick={onOpenPalette}
          className="wsstrip-cmd"
          aria-label={`Open command palette (${kbdHint})`}
        >
          <Command size={12} />
          <span className="wsstrip-cmd-label">Jump</span>
          <span className="wsstrip-cmd-kbd">{kbdHint}</span>
        </button>

        <button
          type="button"
          className="wsstrip-bell"
          onClick={() => navigate("/app/general-chat")}
          aria-label={chatUnread ? `BondChat, ${chatUnread} unread` : "BondChat"}
          title="BondChat"
        >
          <MessageCircle size={14} />
          {chatUnread > 0 && (
            <span className="wsstrip-bell-dot">
              {formatBadgeCount(chatUnread)}
            </span>
          )}
        </button>

        <button
          type="button"
          className="wsstrip-bell"
          onClick={() =>
            navigate(isAuthenticated ? "/app/activity" : "/app/login", {
              state: { from: "/app/activity" },
            })
          }
          aria-label={
            activityUnread
              ? `Bond Pulse, ${activityUnread} unread`
              : "Bond Pulse"
          }
          title="Bond Pulse"
        >
          <Activity size={14} />
          {activityUnread > 0 && (
            <span className="wsstrip-bell-dot">
              {formatBadgeCount(activityUnread)}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default AppWorkspaceTopStrip;
